import { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import {
	Mail,
	Search,
	Download,
	Loader2,
	ChevronLeft,
	ChevronRight,
	Users,
	UserCheck,
	UserMinus,
	Trash2,
	MailX,
	RefreshCw,
	Send,
} from "lucide-react";
import { db, appId } from "@/lib/firebase";
import {
	onNewsletterSubscriptions,
	unsubscribeFromNewsletter,
	deleteNewsletterSubscription,
	markBrevoSynced,
	markWelcomeEmailSent,
	sendNewsletterWelcomeEmail,
	type NewsletterSubscription,
	type NewsletterSubscriptionStatus,
} from "@/lib/newsletter";
import { addBrevoContact } from "@/lib/utils/brevo";

interface AdminNewsletterSubscriptionsProps {
	showNotify?: (message: string, type?: "success" | "error" | "info") => void;
	confirmAction?: (title: string, message: string, action: () => void) => void;
}

type StatusFilter = "all" | NewsletterSubscriptionStatus;

const STATUS_LABELS: Record<NewsletterSubscriptionStatus, string> = {
	active: "Active",
	unsubscribed: "Unsubscribed",
};

const STATUS_COLORS: Record<NewsletterSubscriptionStatus, string> = {
	active: "bg-emerald-100 text-emerald-700",
	unsubscribed: "bg-gray-100 text-gray-500",
};

const SOURCE_LABELS: Record<string, string> = {
	footer_newsletter: "Footer",
	inline_newsletter: "Inline",
};

function formatSource(source: string): string {
	return SOURCE_LABELS[source] ?? source.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(ts: NewsletterSubscription["subscribedAt"] | NewsletterSubscription["welcomeEmailSentAt"]): string {
	if (!ts) return "—";
	const d = typeof (ts as { toDate?: () => Date }).toDate === "function"
		? (ts as { toDate: () => Date }).toDate()
		: new Date(ts as Date);
	return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function exportCsv(rows: NewsletterSubscription[]): void {
	const headers = ["Email", "First Name", "Status", "Source", "Subscribed At", "Unsubscribed At", "Brevo Synced", "Welcome Sent At"];
	const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
	const lines = [
		headers.map(escape).join(","),
		...rows.map((r) =>
			[
				r.email,
				r.firstName ?? "",
				r.status,
				r.source,
				formatDate(r.subscribedAt),
				r.unsubscribedAt ? formatDate(r.unsubscribedAt) : "",
				r.brevoSynced ? "Yes" : "No",
				r.welcomeEmailSentAt ? formatDate(r.welcomeEmailSentAt) : "",
			]
				.map(escape)
				.join(",")
		),
	];
	const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = `newsletter-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
	a.click();
	URL.revokeObjectURL(url);
}

export const AdminNewsletterSubscriptions = ({
	showNotify,
	confirmAction,
}: AdminNewsletterSubscriptionsProps): JSX.Element => {
	const [subscriptions, setSubscriptions] = useState<NewsletterSubscription[]>([]);
	const [loading, setLoading] = useState(true);
	const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
	const [search, setSearch] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [actionInProgress, setActionInProgress] = useState<string | null>(null);
	const [brevoApiKeyFromDb, setBrevoApiKeyFromDb] = useState("");
	const [brevoListId, setBrevoListId] = useState<number | null>(null);
	const [syncAllInProgress, setSyncAllInProgress] = useState(false);
	const [welcomeInProgress, setWelcomeInProgress] = useState<string | null>(null);

	useEffect(() => {
		const unsub = onNewsletterSubscriptions((data) => {
			setSubscriptions(data);
			setLoading(false);
		});
		return unsub;
	}, []);

	// API key from settings/brevo (root-only)
	useEffect(() => {
		let cancelled = false;
		const brevoRef = doc(db, "artifacts", appId, "settings", "brevo");
		getDoc(brevoRef)
			.then((snap) => {
				if (cancelled || !snap.exists()) return;
				const d = snap.data();
				const key = (d?.apiKey as string)?.trim() ?? "";
				if (key) setBrevoApiKeyFromDb(key);
			})
			.catch(() => {});
		return () => { cancelled = true; };
	}, []);

	// List ID from settings/contact (same as before – already used by Contact form)
	useEffect(() => {
		let cancelled = false;
		const contactRef = doc(db, "artifacts", appId, "settings", "contact");
		getDoc(contactRef)
			.then((snap) => {
				if (cancelled || !snap.exists()) return;
				const d = snap.data();
				const raw = d?.brevoNewsletterListId;
				if (raw != null && raw !== "") {
					const id = typeof raw === "number" ? raw : parseInt(String(raw), 10);
					if (Number.isInteger(id) && id > 0) setBrevoListId(id);
				}
			})
			.catch(() => {});
		return () => { cancelled = true; };
	}, []);

	// Reset to page 1 when filters change
	useEffect(() => {
		setCurrentPage(1);
	}, [statusFilter, search]);

	const filtered = useMemo(() => {
		let result = subscriptions;
		if (statusFilter !== "all") {
			result = result.filter((s) => s.status === statusFilter);
		}
		if (search.trim()) {
			const q = search.trim().toLowerCase();
			result = result.filter(
				(s) =>
					s.email.toLowerCase().includes(q) ||
					(s.firstName ?? "").toLowerCase().includes(q)
			);
		}
		return result;
	}, [subscriptions, statusFilter, search]);

	const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
	const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

	// Stats
	const totalCount = subscriptions.length;
	const activeCount = subscriptions.filter((s) => s.status === "active").length;
	const unsubscribedCount = subscriptions.filter((s) => s.status === "unsubscribed").length;

	const handleUnsubscribe = (sub: NewsletterSubscription) => {
		const doUnsubscribe = async () => {
			setActionInProgress(sub.id);
			try {
				await unsubscribeFromNewsletter(sub.id);
				showNotify?.(`${sub.email} has been unsubscribed`, "success");
			} catch (err) {
				console.error(`[AdminNewsletterSubscriptions] Failed to unsubscribe ${sub.email}:`, err);
				showNotify?.("Failed to unsubscribe. Please try again.", "error");
			} finally {
				setActionInProgress(null);
			}
		};

		if (confirmAction) {
			confirmAction(
				"Unsubscribe",
				`Are you sure you want to unsubscribe ${sub.email}? This will mark them as unsubscribed but preserve the record.`,
				doUnsubscribe
			);
		} else {
			doUnsubscribe();
		}
	};

	const handleDelete = (sub: NewsletterSubscription) => {
		const doDelete = async () => {
			setActionInProgress(sub.id);
			try {
				await deleteNewsletterSubscription(sub.id);
				showNotify?.(`${sub.email} has been permanently deleted`, "success");
			} catch (err) {
				console.error(`[AdminNewsletterSubscriptions] Failed to delete ${sub.email}:`, err);
				showNotify?.("Failed to delete subscriber. Please try again.", "error");
			} finally {
				setActionInProgress(null);
			}
		};

		if (confirmAction) {
			confirmAction(
				"Delete subscriber",
				`Permanently delete ${sub.email}? This cannot be undone.`,
				doDelete
			);
		} else {
			doDelete();
		}
	};

	const handleSendWelcomeEmail = async (sub: NewsletterSubscription) => {
		if (!brevoApiKeyFromDb?.trim()) {
			showNotify?.("Configure Brevo API key in Settings → Brevo API (Root only).", "error");
			return;
		}
		setWelcomeInProgress(sub.id);
		try {
			await sendNewsletterWelcomeEmail(brevoApiKeyFromDb, sub.email, sub.firstName);
			await markWelcomeEmailSent(sub.id);
			showNotify?.(`Welcome email sent to ${sub.email}`, "success");
		} catch (err) {
			console.error(`[AdminNewsletterSubscriptions] Welcome email failed for ${sub.email}:`, err);
			showNotify?.(
				err instanceof Error ? err.message : "Failed to send welcome email. Check Brevo API key.",
				"error"
			);
		} finally {
			setWelcomeInProgress(null);
		}
	};

	const canSyncToBrevo = !!brevoApiKeyFromDb && brevoListId != null && brevoListId > 0;
	const unsyncedActive = useMemo(
		() => subscriptions.filter((s) => s.status === "active" && !s.brevoSynced),
		[subscriptions]
	);

	const handleSyncToBrevo = async (sub: NewsletterSubscription) => {
		if (!canSyncToBrevo) {
			showNotify?.("Configure Brevo API key in Settings → Brevo API (Root only) and list ID in Settings → Contact.", "error");
			return;
		}
		setActionInProgress(sub.id);
		try {
			await addBrevoContact({
				apiKey: brevoApiKeyFromDb,
				email: sub.email,
				firstName: sub.firstName ?? undefined,
				listIds: [brevoListId],
			});
			try {
				await sendNewsletterWelcomeEmail(brevoApiKeyFromDb, sub.email, sub.firstName);
				await markWelcomeEmailSent(sub.id);
			} catch (welcomeErr) {
				console.warn(
					`[AdminNewsletterSubscriptions] Welcome email failed for ${sub.email}, contact still synced:`,
					welcomeErr instanceof Error ? welcomeErr.message : welcomeErr
				);
			}
			await markBrevoSynced(sub.id, true);
			showNotify?.(`${sub.email} synced to Brevo`, "success");
		} catch (err) {
			console.error(`[AdminNewsletterSubscriptions] Brevo sync failed for ${sub.email}:`, err);
			showNotify?.(
				err instanceof Error ? err.message : "Failed to sync to Brevo. Check Settings → Brevo API.",
				"error"
			);
		} finally {
			setActionInProgress(null);
		}
	};

	const handleSyncAllUnsynced = async () => {
		if (!canSyncToBrevo || unsyncedActive.length === 0) return;
		setSyncAllInProgress(true);
		let ok = 0;
		let fail = 0;
		for (const sub of unsyncedActive) {
			try {
				await addBrevoContact({
					apiKey: brevoApiKeyFromDb,
					email: sub.email,
					firstName: sub.firstName ?? undefined,
					listIds: [brevoListId!],
				});
				try {
					await sendNewsletterWelcomeEmail(brevoApiKeyFromDb, sub.email, sub.firstName);
					await markWelcomeEmailSent(sub.id);
				} catch (welcomeErr) {
					console.warn(
						`[AdminNewsletterSubscriptions] Welcome email failed for ${sub.email}, contact still synced:`,
						welcomeErr instanceof Error ? welcomeErr.message : welcomeErr
					);
				}
				await markBrevoSynced(sub.id, true);
				ok++;
			} catch {
				fail++;
			}
			await new Promise((r) => setTimeout(r, 300));
		}
		setSyncAllInProgress(false);
		if (ok > 0) showNotify?.(`${ok} subscriber(s) synced to Brevo.`, "success");
		if (fail > 0) showNotify?.(`${fail} subscriber(s) failed to sync. Check console for details.`, "error");
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center py-24">
				<Loader2 size={32} className="animate-spin text-blue-500" />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">Newsletter Subscribers</h1>
					<p className="text-sm text-gray-500 mt-0.5">
						Manage email newsletter subscriptions and export subscriber lists.
					</p>
				</div>
				<div className="flex items-center gap-2">
					{unsyncedActive.length > 0 && (
						<button
							type="button"
							onClick={handleSyncAllUnsynced}
							disabled={!canSyncToBrevo || syncAllInProgress}
							title={!canSyncToBrevo ? "Set Brevo API key in Settings → Brevo API (Root only) and list ID in Settings → Contact" : undefined}
							className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
						>
							{syncAllInProgress ? (
								<Loader2 size={16} className="animate-spin" />
							) : (
								<RefreshCw size={16} />
							)}
							Sync unsynced to Brevo
							<span className="text-xs bg-emerald-500/80 text-white px-1.5 py-0.5 rounded-full font-medium">
								{unsyncedActive.length}
							</span>
						</button>
					)}
					<button
						type="button"
						onClick={() => exportCsv(filtered)}
						disabled={filtered.length === 0}
						className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
					>
						<Download size={16} />
						Export CSV
						{filtered.length > 0 && (
							<span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full font-medium">
								{filtered.length}
							</span>
						)}
					</button>
				</div>
			</div>

			{/* Stats row */}
			<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
				<div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
					<div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
						<Users size={18} className="text-blue-600" />
					</div>
					<div>
						<p className="text-2xl font-bold text-gray-900">{totalCount}</p>
						<p className="text-xs text-gray-500 font-medium">Total subscribers</p>
					</div>
				</div>
				<div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
					<div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
						<UserCheck size={18} className="text-emerald-600" />
					</div>
					<div>
						<p className="text-2xl font-bold text-gray-900">{activeCount}</p>
						<p className="text-xs text-gray-500 font-medium">Active</p>
					</div>
				</div>
				<div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
					<div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
						<UserMinus size={18} className="text-gray-500" />
					</div>
					<div>
						<p className="text-2xl font-bold text-gray-900">{unsubscribedCount}</p>
						<p className="text-xs text-gray-500 font-medium">Unsubscribed</p>
					</div>
				</div>
			</div>

			{/* Filters */}
			<div className="flex flex-wrap gap-3 items-center">
				<div className="relative flex-1 min-w-48">
					<Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
					<input
						type="search"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Search by email or name…"
						className="w-full rounded-lg border border-gray-300 pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
				</div>
				<select
					value={statusFilter}
					onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
					className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
				>
					<option value="all">All statuses</option>
					<option value="active">Active</option>
					<option value="unsubscribed">Unsubscribed</option>
				</select>
				<select
					value={pageSize}
					onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
					className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
				>
					<option value={10}>10 / page</option>
					<option value={20}>20 / page</option>
					<option value={50}>50 / page</option>
					<option value={100}>100 / page</option>
				</select>
			</div>

			{/* Table */}
			{filtered.length === 0 ? (
				<div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-16 text-center">
					<div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
						<Mail size={24} className="text-gray-400" />
					</div>
					<h3 className="text-base font-semibold text-gray-700 mb-1">
						{search || statusFilter !== "all" ? "No matching subscribers" : "No subscribers yet"}
					</h3>
					<p className="text-sm text-gray-400">
						{search || statusFilter !== "all"
							? "Try adjusting your search or filter."
							: "Subscribers will appear here once people sign up via the footer newsletter form."}
					</p>
				</div>
			) : (
				<div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b border-gray-100 bg-gray-50/60">
									<th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
									<th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
									<th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
									<th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Source</th>
									<th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Subscribed</th>
									<th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Brevo</th>
									<th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Welcome</th>
									<th className="px-4 py-3" aria-label="Actions" />
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-50">
								{paginated.map((sub) => (
									<tr key={sub.id} className="hover:bg-gray-50/50 transition-colors">
										<td className="px-4 py-3 font-medium text-gray-900 max-w-xs truncate">
											{sub.email}
										</td>
										<td className="px-4 py-3 text-gray-600">
											{sub.firstName || <span className="text-gray-300">—</span>}
										</td>
										<td className="px-4 py-3">
											<span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[sub.status]}`}>
												{STATUS_LABELS[sub.status]}
											</span>
										</td>
										<td className="px-4 py-3 text-gray-500 text-xs">
											{formatSource(sub.source)}
										</td>
										<td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
											{formatDate(sub.subscribedAt)}
										</td>
										<td className="px-4 py-3">
											{sub.brevoSynced ? (
												<span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
													<span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
													Synced
												</span>
											) : (
												<span className="text-xs text-gray-300">—</span>
											)}
										</td>
										<td className="px-4 py-3">
											<div className="flex items-center gap-2">
												<span className="text-xs text-gray-500 whitespace-nowrap">
													{sub.welcomeEmailSentAt ? formatDate(sub.welcomeEmailSentAt) : "—"}
												</span>
												{sub.status === "active" && (
													<button
														type="button"
														title={sub.welcomeEmailSentAt ? "Resend welcome email" : "Send welcome email"}
														onClick={() => handleSendWelcomeEmail(sub)}
														disabled={!brevoApiKeyFromDb?.trim() || welcomeInProgress === sub.id}
														className="p-1.5 rounded-md text-gray-400 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
													>
														{welcomeInProgress === sub.id ? (
															<Loader2 size={14} className="animate-spin" />
														) : (
															<Send size={14} />
														)}
													</button>
												)}
											</div>
										</td>
										<td className="px-4 py-3">
											<div className="flex items-center gap-1 justify-end">
												{sub.status === "active" && !sub.brevoSynced && (
													<button
														type="button"
														title="Sync to Brevo"
														onClick={() => handleSyncToBrevo(sub)}
														disabled={!canSyncToBrevo || actionInProgress === sub.id}
														className="p-1.5 rounded-md text-gray-400 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400"
													>
														{actionInProgress === sub.id ? (
															<Loader2 size={14} className="animate-spin" />
														) : (
															<RefreshCw size={14} />
														)}
													</button>
												)}
												{sub.status === "active" && (
													<button
														type="button"
														title="Unsubscribe"
														onClick={() => handleUnsubscribe(sub)}
														disabled={actionInProgress === sub.id}
														className="p-1.5 rounded-md text-gray-400 hover:bg-amber-50 hover:text-amber-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400"
													>
														{actionInProgress === sub.id ? (
															<Loader2 size={14} className="animate-spin" />
														) : (
															<MailX size={14} />
														)}
													</button>
												)}
												<button
													type="button"
													title="Delete permanently"
													onClick={() => handleDelete(sub)}
													disabled={actionInProgress === sub.id}
													className="p-1.5 rounded-md text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
												>
													{actionInProgress === sub.id ? (
														<Loader2 size={14} className="animate-spin" />
													) : (
														<Trash2 size={14} />
													)}
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/40">
							<p className="text-xs text-gray-500">
								{(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filtered.length)} of{" "}
								{filtered.length} subscribers
							</p>
							<div className="flex items-center gap-1">
								<button
									type="button"
									onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
									disabled={currentPage === 1}
									className="p-1.5 rounded-md border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
									aria-label="Previous page"
								>
									<ChevronLeft size={14} />
								</button>
								<span className="text-xs text-gray-600 px-2 font-medium">
									{currentPage} / {totalPages}
								</span>
								<button
									type="button"
									onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
									disabled={currentPage === totalPages}
									className="p-1.5 rounded-md border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
									aria-label="Next page"
								>
									<ChevronRight size={14} />
								</button>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
